import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'antd';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
// ---
import { draftToHtml } from 'utils/helpers';
import { generatePinyin, addPinyinToHtml } from 'shared/utils/pinyin';
import { HtmlDoc } from 'shared/utils/html-doc';
import { getTextsDiff } from 'utils/pinyin/get-texts-diff';
import { EditCharPinyin } from './EditCharPinyin';
import { HtmlWithPinyinContent } from 'shared/components/HtmlWithPinyin';
import styles from './PinyinEditor.less';

const ERHUAYIN_CHARS = ['兒', '儿'];

export const PinyinEditor = (props) => {
  const {
    defaultFontSize,
    isEditing = false,
    draftEditorState,
    pinyinMap: pinyinMapFromProps,
    onChange,
    onClose,
  } = props;

  const [lastHtmlDoc, setLastHtmlDoc] = useState(() => {
    if (draftEditorState === null) {
      return null;
    }

    return getHtmlDoc(draftEditorState);
  });

  const [pinyinMap, setPinyinMap] = useState(pinyinMapFromProps);

  const setPinyinMapFromHtmlDoc = async (htmlDoc) => {
    const pureText = htmlDoc.getPureText();
    const pinyinMap = await computePinyinMap(pureText);

    setPinyinMap((prev) => {
      if (!isEmpty(prev)) {
        return prev;
      }

      return pinyinMap;
    });
  };

  const [editingCharDetails, setEditingCharDetails] = useState(null);

  const draftHtml = useMemo(() => {
    if (!draftEditorState) {
      return null;
    }

    return draftToHtml(draftEditorState);
  }, [draftEditorState]);

  const htmlWithPinyin = useMemo(() => {
    if (draftHtml === null) {
      return null;
    }

    return addPinyinToHtml({ html: draftHtml, pinyinMap });
  }, [draftHtml, pinyinMap]);

  const handleClick = (e) => {
    const rubyTextWithPinyin = e.target.closest('.withPinyin');

    if (!rubyTextWithPinyin) {
      setEditingCharDetails(null);
      return;
    }

    setEditingCharDetails(getEditingCharDetails(pinyinMap, rubyTextWithPinyin));
  };

  const handleEndEdit = () => {
    onClose();
  };

  const handleEditCharPinyin = (charOffset, pinyin) => {
    setPinyinMap((prevPinyinMap) => {
      const editingChar = editingCharDetails.char;
      const currentEntry = prevPinyinMap[charOffset];

      const updatedPinyinMap = {
        ...prevPinyinMap,
        [charOffset]: {
          ...currentEntry,
          pinyin,
          span: editingChar.length,
        },
      };

      if (
        pinyin === '' &&
        currentEntry?.pinyin !== pinyin &&
        ERHUAYIN_CHARS.includes(editingChar)
      ) {
        const prevEntry = prevPinyinMap[charOffset - 1];

        updatedPinyinMap[charOffset - 1] = {
          ...prevEntry,
          pinyin: `${prevEntry.pinyin}r`,
          span: prevEntry.span + 1,
        };
      }

      return updatedPinyinMap;
    });
  };

  const handleCloseEditCharPinyin = () => {
    setEditingCharDetails(null);
  };

  useEffect(() => {
    const handleInitialDraftHtml = () => {
      if (!draftHtml) {
        return;
      }

      const lastHtmlDoc = HtmlDoc.fromPartialHtml(draftHtml);
      setLastHtmlDoc(lastHtmlDoc);
      setPinyinMapFromHtmlDoc(lastHtmlDoc);
    };

    const handleDraftHtmlChange = async () => {
      if (!lastHtmlDoc) {
        return handleInitialDraftHtml();
      }

      const lastPureText = lastHtmlDoc.getPureText();
      const currentHtmlDoc = HtmlDoc.fromPartialHtml(draftHtml);
      const pureText = currentHtmlDoc.getPureText();
      const textDiffs = getTextsDiff(lastPureText, pureText);
      const newPinyinMap = await updatePinyinMapWithTextDiffs(pinyinMap, textDiffs);
      setPinyinMap(newPinyinMap);
      setLastHtmlDoc(currentHtmlDoc);
    };

    handleDraftHtmlChange();
  }, [draftHtml]);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    setEditingCharDetails(null);
  }, [isEditing]);

  useEffect(() => {
    onChange(pinyinMap);
  }, [pinyinMap, onChange]);

  if (!isEditing) {
    return null;
  }

  return (
    <div className={styles.pinyinEditor}>
      <div className={styles.topBar}>
        <Button type="primary" onClick={handleEndEdit}>
          Edit Content
        </Button>
        <span>Press any Chinese character to edit pinyin</span>
      </div>

      {htmlWithPinyin && (
        <div className={styles.pinyinEditable} onClick={handleClick}>
          <HtmlWithPinyinContent defaultFontSize={defaultFontSize} html={htmlWithPinyin} />

          {editingCharDetails && (
            <EditCharPinyin
              key={editingCharDetails.pinyinEntry.index}
              details={editingCharDetails}
              onChange={handleEditCharPinyin}
              onClose={handleCloseEditCharPinyin}
            />
          )}
        </div>
      )}
    </div>
  );
};

PinyinEditor.propTypes = {
  defaultFontSize: PropTypes.number.isRequired,
  isEditing: PropTypes.bool,
  draftEditorState: PropTypes.object,
  pinyinMap: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const getHtmlDoc = (editorState) => {
  return HtmlDoc.fromPartialHtml(draftToHtml(editorState));
};

const updatePinyinMapWithTextDiffs = async (pinyinMap, textDiffs) => {
  let charOffset = 0;
  let newPinyinMap = pinyinMap;

  for (let textDiff of textDiffs) {
    const result = await handleTextDiff(newPinyinMap, textDiff, charOffset);
    charOffset = result.charOffset;
    newPinyinMap = result.pinyinMap;
  }

  return newPinyinMap;
};

const handleTextDiff = async (pinyinMap, textDiff, charOffset) => {
  const newPinyinMap = { ...pinyinMap };
  const [changeType, text] = textDiff;

  switch (changeType) {
  case -1:
    return handleDeleteTextDiff(newPinyinMap, text, charOffset);
  case 1:
    return handleInsertTextDiff(newPinyinMap, text, charOffset);
  case 0:
    return handleUnchangedTextDiff(newPinyinMap, text, charOffset);
  }

  return { charOffset, pinyinMap: newPinyinMap };
};

const handleDeleteTextDiff = (pinyinMap, text, charOffset) => {
  for (let i = 0; i < text.length; i++) {
    delete pinyinMap[charOffset + i];
  }

  const modifiedEntries = [];

  for (let entry of Object.values(pinyinMap)) {
    const { index } = entry;

    if (index > charOffset) {
      modifiedEntries.push({
        ...entry,
        index: index - text.length,
      });

      delete pinyinMap[index];
    }
  }

  Object.assign(pinyinMap, keyBy(modifiedEntries, 'index'));
  return { charOffset, pinyinMap };
};

const handleInsertTextDiff = async (pinyinMap, text, charOffset) => {
  const modifiedEntries = [];

  for (let entry of Object.values(pinyinMap)) {
    const { index } = entry;

    if (index >= charOffset) {
      modifiedEntries.push({
        ...entry,
        index: index + text.length,
      });

      delete pinyinMap[index];
    }
  }

  Object.assign(pinyinMap, keyBy(modifiedEntries, 'index'));
  Object.assign(pinyinMap, await computePinyinMap(text, charOffset));
  return { charOffset: charOffset + text.length, pinyinMap };
};

const handleUnchangedTextDiff = (pinyinMap, text, charOffset) => {
  return { charOffset: charOffset + text.length, pinyinMap };
};

const computePinyinMap = async (pureText, baseOffset = 0) => {
  const pinyin = await generatePinyin(pureText);

  let charOffset = baseOffset;

  return pinyin.reduce((acc, charPinyin) => {
    if (pureText[charOffset - baseOffset] === charPinyin[0]) {
      charOffset += charPinyin.length;
      return acc;
    }

    acc[charOffset] = {
      pinyin: charPinyin,
      index: charOffset,
      span: 1,
    };

    charOffset += 1;
    return acc;
  }, {});
};

const getEditingCharDetails = (pinyinMap, rubyTextWithPinyin) => {
  const textRect = rubyTextWithPinyin.getBoundingClientRect();
  const containerRect = rubyTextWithPinyin
    .closest(`.${styles.pinyinEditable}`)
    .getBoundingClientRect();

  const charOffset = parseInt(rubyTextWithPinyin.dataset.charOffset);
  const char = rubyTextWithPinyin.dataset.char;
  const pinyinEntry = pinyinMap[charOffset];

  return {
    boundingRect: {
      left: textRect.left - containerRect.left,
      top: textRect.top - containerRect.top,
      width: textRect.width,
      height: textRect.height,
    },
    pinyinEntry,
    char,
  };
};
